// Thin fetch wrapper. All requests join the Authorization: Bearer <token> header
// when an auth token is present in the store, and surface 401/403 as typed errors
// so components can react (re-login, redirect to project list).

import type { Project, ProjectRole, Remark } from '@ladd-screenshot/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface PublicConfig {
  turnstileEnabled: boolean;
  turnstileSiteKey?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  turnstileToken?: string;
}

export interface LoginResponse {
  token: string;
  email: string;
  expiresAt: number;
}

export interface ListProjectsResponse {
  projects: Project[];
}

export interface ProjectDetailResponse {
  project: Project;
  role: ProjectRole;
}

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  if (token) localStorage.setItem('ladd-screenshot.token', token);
  else localStorage.removeItem('ladd-screenshot.token');
}

export function restoreAuthToken(): string | null {
  authToken = localStorage.getItem('ladd-screenshot.token');
  return authToken;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await rawRequest(path, init);
  return (await res.json()) as T;
}

async function rawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // body wasn't JSON — keep statusText
    }
    throw new ApiError(res.status, message);
  }
  return res;
}

export const api = {
  getConfig: () => request<PublicConfig>('/api/config'),

  login: (body: LoginRequest) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  verify: () =>
    request<{ email: string; expiresAt: number }>('/api/auth/verify', { method: 'POST' }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  listProjects: () => request<ListProjectsResponse>('/api/v1/projects'),

  getProject: (projectId: string) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${encodeURIComponent(projectId)}`),

  listRemarks: (projectId: string) =>
    request<{ remarks: Remark[] }>(`/api/v1/projects/${encodeURIComponent(projectId)}/remarks`),

  createRemark: (projectId: string, body: Partial<Remark> & { author: string; text: string; variantId: string; resolutionName: string; targetSlug: string }) =>
    request<Remark>(`/api/v1/projects/${encodeURIComponent(projectId)}/remarks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateRemark: (remarkId: string, patch: Partial<Pick<Remark, 'status' | 'text' | 'rect' | 'extract'>>) =>
    request<Remark>(`/api/v1/remarks/${encodeURIComponent(remarkId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteRemark: (remarkId: string) =>
    rawRequest(`/api/v1/remarks/${encodeURIComponent(remarkId)}`, { method: 'DELETE' }).then(
      () => undefined,
    ),

  addComment: (remarkId: string, body: { author: string; text: string }) =>
    request<{ id: string; remarkId: string; author: string; text: string; createdAt: number }>(
      `/api/v1/remarks/${encodeURIComponent(remarkId)}/comments`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listTargets: (projectId: string) =>
    request<{
      targets: Array<{
        id: string;
        projectId: string;
        slug: string;
        path: string;
        fullPage: number;
        triggerLazyLoad: number;
        excludeVariants: string[] | null;
        interactions: unknown;
        config: unknown;
        capturesCount: number;
        remarksCount: number;
      }>;
    }>(`/api/v1/projects/${encodeURIComponent(projectId)}/targets`),

  listCaptures: (projectId: string, targetId?: string) => {
    const qs = targetId ? `?targetId=${encodeURIComponent(targetId)}` : '';
    return request<{ captures: Array<Record<string, unknown>> }>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/captures${qs}`,
    );
  },

  getCapture: (captureId: string) =>
    request<{ capture: Record<string, unknown> }>(
      `/api/v1/captures/${encodeURIComponent(captureId)}`,
    ),

  captureImageUrl: (captureId: string): string =>
    `/api/v1/captures/${encodeURIComponent(captureId)}/image`,

  // Fetches the protected image and exposes it as a blob: URL so an <img>
  // tag (which can't carry our Bearer header) can render it. Caller must
  // URL.revokeObjectURL() when the image unmounts.
  fetchCaptureImageObjectUrl: async (captureId: string): Promise<string> => {
    const res = await rawRequest(
      `/api/v1/captures/${encodeURIComponent(captureId)}/image`,
    );
    return URL.createObjectURL(await res.blob());
  },
};
